import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import Anchor from '../../../commonComponents/Anchor';
import Notifications from '../../../services/notifications';
import Request from '../../../services/request';
import CertificateProp from '../propTypes/certificateProp';
import CourseProp from '../propTypes/courseProp';
import ShareModal from '../modal/ShareModal';
import AppProductProp from '../../profile/propTypes/appProductProp';
import {
  compose,
  displayMoney,
  IN_APP_PRODUCT_TYPE,
  resolveDocumentUrl,
} from '../../../services/utilityFunctions';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Loader from '../../../services/loader';
import withCSRF from '../../../commonComponents/withCSRF';
import IsAcademyPublic from '../../../commonComponents/IsAcademyPublic';
import JourneyProp from '../../journey/propTypes/journeyProp';
import sessionProp from '../../sessions/propTypes/sessionProp';
import CertificateButton from './CertificateButton';
import CertificateLockOverlay from '../../../commonComponents/CertificateLockOverlay';

function CertificateDisplay(props) {
  const loader = useRef(new Loader('#certificateLayout'));
  const { loggedUser } = useContext(LoggedUserContext);
  const {
    t,
    _csrf,
    hasAcademyHeader,
    course,
    certificate,
    onPrint,
    onEmail,
    appProducts,
    typeCertificate,
    journey,
    session,
  } = props;

  const [isFetching, setFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCertificate, setSavedCertificate] = useState(certificate);

  const [modalInfoShare, setModalInfoShare] = useState({ show: false });
  /**
   * BRAVON-6332 Phase 2b, Stage 6 — publish the PUBLIC copy of the certificate
   * image before the share link leaves this page. The image itself is private
   * and reaches the browser as a presigned url that expires in an hour, which
   * the OG scrapers behind a shared link can do nothing with.
   *
   * Idempotent, and deliberately fire-and-forget: the link works either way —
   * only its preview card would be missing — so a failure must not stop the
   * modal from opening.
   */
  const publishShareImage = id => {
    if (!id) return;
    new Request(_csrf).post(`/certificate/${id}/share`).fail(err => {
      Notifications.showErrorFromRequest(err, 'error_fetchCertificate');
    });
  };

  const showModalShare = () => {
    publishShareImage(savedCertificate && savedCertificate._id);
    setModalInfoShare({ show: true });
  };
  const hideModalShare = () => setModalInfoShare({ show: false });

  useEffect(() => {
    if (loader.current) {
      if (isFetching) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isFetching]);

  const hasImage =
    savedCertificate && savedCertificate.image && savedCertificate.image.url;
  const isLocked = savedCertificate && savedCertificate.isLocked;

  const onReload = async () => {
    setIsLoading(true);
    await axios
      .post(
        `/certificate/${certificate._id}/preview`,
        {},
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(({ data: response }) => {
        setSavedCertificate(response.data);
        setIsLoading(false);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchCertificate');
        setIsLoading(false);
      });
  };

  const priceForProduct = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return displayMoney(
      product.userCurrency.total,
      product.userCurrency.symbol,
    );
  };

  const isProductFree = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.total === 0;
  };

  const productCurrency = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.code;
  };

  const { protocol, host } =
    typeof window !== 'undefined' ? window.location : {};
  const fallBackShareUrl = `${protocol}//${host}/certificate/${savedCertificate.code}`;

  const requestFreeCertificate = () => {
    setFetching(true);
    const params = {
      owner: loggedUser._id,
      amount: 0,
      currency: productCurrency(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE),
    };

    new Request(_csrf)
      .post(`/certificate/${certificate._id}/email`, params)
      .done(({ data }) => {
        setFetching(false);
        Notifications.showNotificationSuccess(
          t('success'),
          t('certificate_purchased'),
        );
        window.open(resolveDocumentUrl(data.link), '_blank');
      })
      .fail(err => {
        setFetching(false);
        Notifications.showErrorFromRequest(err, 'error_certificatePurchase');
      });
  };

  const messageDescription = () => {
    if (typeCertificate === 'course') {
      return t('certificate_youWon');
    }
    if (typeCertificate === 'journey') {
      return t('certificate_completedJourney');
    }
    if (typeCertificate === 'session') {
      return t('certificate_completedSession');
    }
    return '';
  };

  const titleCertificate = () => {
    if (typeCertificate === 'course') {
      return course.name;
    }
    if (typeCertificate === 'journey') {
      return journey.title;
    }
    if (typeCertificate === 'session') {
      return session.name;
    }
    return '';
  };

  const handleLockBtn = () => {
    // When a certificate for session is locked since user didn't answer the survey
    if (certificate.survey && certificate.surveyContext) {
      window.location.href = `/survey/${certificate?.survey}?context=${certificate.surveyContext}&backURL=${window.location.pathname}`;
    }
  };

  return (
    <div id="certificateLayout" className="certificate-display">
      {modalInfoShare.show && (
        <ShareModal
          url={savedCertificate.share || fallBackShareUrl}
          onClose={hideModalShare}
        />
      )}
      {isLocked && (
        <CertificateLockOverlay
          message={t('certificate.unlock.message.session')}
          title={t('certificate.unlock.title')}
          buttonMessage={t('certificate.unlock.session.button')}
          onClick={handleLockBtn}
        />
      )}
      <div className="m-portlet m-portlet--rounded">
        <div className="header-top">
          <img src="/images/certificate.svg" alt="certificate preview" />
          <h3>{t('certificate_congrats')}</h3>
        </div>
        <div className="m-portlet__body">
          <p>
            {messageDescription()}
            <span className="text-dynamic" style={{ marginLeft: '0.5rem' }}>
              {titleCertificate()}
            </span>
          </p>
          <div className="certificate-img" style={{ position: 'relative' }}>
            {!isLocked && hasImage ? (
              <>
                <img
                  className="certificate-preview"
                  alt="certificate preview"
                  src={savedCertificate.image.thumb}
                />
                <div className="hover-overlay">
                  <Anchor
                    href={`/certificate/${savedCertificate._id}`}
                    className="certificate-overlay-button"
                  >
                    <img
                      style={{ width: '60%', height: '60%' }}
                      alt="refresh arrow"
                      src="/images/academy/certificate/detail.svg"
                    />
                  </Anchor>
                </div>
              </>
            ) : (
              <>
                {!isLocked && (
                  <div className="certificate-no-preview">
                    {t('certificate_noPreview')}
                  </div>
                )}
                <div className="certificate-placeholder">
                  <img
                    className="certificate-placeholder"
                    alt="certificate preview"
                    src="/images/academy/certificate/certificate.png"
                  />
                  {!isLocked && (
                    <button
                      type="button"
                      className={`certificate-overlay-button ${
                        isLoading ? 'loading' : ''
                      }`}
                      onClick={onReload}
                    >
                      <img
                        alt="refresh arrow"
                        src="/images/academy/certificate/load.svg"
                      />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <br />
          <br />
          {loggedUser && !isLocked && (
            <div className="certificate-buttons">
              {priceForProduct(IN_APP_PRODUCT_TYPE.CERTIFICATE) && (
                <button type="button" onClick={onPrint}>
                  <CertificateButton
                    t={t}
                    type="letter"
                    price={priceForProduct(IN_APP_PRODUCT_TYPE.CERTIFICATE)}
                  />
                </button>
              )}
              {(priceForProduct(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE) ||
                isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)) && (
                <button
                  type="button"
                  onClick={
                    isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                      ? requestFreeCertificate
                      : onEmail
                  }
                >
                  <CertificateButton
                    t={t}
                    type="email"
                    price={
                      isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                        ? ''
                        : priceForProduct(
                            IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE,
                          )
                    }
                  />
                </button>
              )}
              {hasAcademyHeader && (
                <button type="button" onClick={showModalShare}>
                  <CertificateButton t={t} type="share" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

CertificateDisplay.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  hasAcademyHeader: PropTypes.bool.isRequired,
  onPrint: PropTypes.func.isRequired,
  onEmail: PropTypes.func.isRequired,
  certificate: PropTypes.shape(CertificateProp.propType).isRequired,
  course: PropTypes.shape(CourseProp.propType),
  journey: PropTypes.shape(JourneyProp.propType),
  session: PropTypes.shape(sessionProp.propType),
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  typeCertificate: PropTypes.string,
};

CertificateDisplay.defaultProps = {
  course: {},
  journey: {},
  session: {},
  typeCertificate: '',
};

export default compose(
  translate,
  withCSRF,
  IsAcademyPublic,
)(CertificateDisplay);
